/**
 * Gingerbread! — Dark Web Full-Text Search
 *
 * Google-style search over the 32,000+ indexed .onion pages.
 * Restricted to @monadnockcyber.ai accounts.
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import api from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SearchResult {
  url: string
  domain: string
  title: string
  snippet: string
  updated_on: string
  score: number
}

interface SearchResponse {
  query: string
  total_hits: number
  results: SearchResult[]
}

interface LumenNotice {
  notice_id: number
  title: string
  notice_type: string
  date_received: string
  sender_name: string
  recipient_name: string
  action_taken: string
  topics: string[]
  url_count: number
  snippet: string
  score: number
}

interface LumenSearchResponse {
  query: string
  total_hits: number
  results: LumenNotice[]
}

type Mode = 'tor' | 'lumen'

// ─── Highlight renderer ───────────────────────────────────────────────────────
// Backend wraps matches in [MATCH]...[/MATCH] for subject search,
// and <em> tags for standard ES highlight. We normalize both.

function renderSnippet(raw: string) {
  if (!raw) return null
  // Replace ES <em> tags and [MATCH] markers with styled spans
  const parts: (string | JSX.Element)[] = []
  const normalized = raw
    .replace(/\[MATCH\]/g, '<em>')
    .replace(/\[\/MATCH\]/g, '</em>')

  const segments = normalized.split(/(<em>.*?<\/em>)/g)
  segments.forEach((seg, i) => {
    if (seg.startsWith('<em>') && seg.endsWith('</em>')) {
      const inner = seg.slice(4, -5)
      parts.push(
        <mark key={i} className="bg-ginger-500/20 text-ginger-300 rounded px-0.5 not-italic">
          {inner}
        </mark>
      )
    } else {
      parts.push(<span key={i} className="text-dark-300">{seg}</span>)
    }
  })
  return <>{parts}</>
}

// ─── Lumen type badge colors ──────────────────────────────────────────────────

const LUMEN_COLORS: Record<string, string> = {
  Defamation:         'text-red-400 bg-red-950/40 border-red-500/30',
  PrivateInformation: 'text-orange-400 bg-orange-950/40 border-orange-500/30',
  CourtOrder:         'text-purple-400 bg-purple-950/40 border-purple-500/30',
  GovernmentRequest:  'text-yellow-400 bg-yellow-950/40 border-yellow-500/30',
  DMCA:               'text-blue-400 bg-blue-950/40 border-blue-500/30',
  Trademark:          'text-cyan-400 bg-cyan-950/40 border-cyan-500/30',
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DarkWebSearch() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const inputRef = useRef<HTMLInputElement>(null)

  const [mode, setMode] = useState<Mode>('tor')
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [committed, setCommitted] = useState(searchParams.get('q') || '')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<SearchResponse | null>(null)
  const [lumenResults, setLumenResults] = useState<LumenSearchResponse | null>(null)
  const [error, setError] = useState('')
  const [size, setSize] = useState(20)

  const isAllowed = user?.email?.toLowerCase().endsWith('@monadnockcyber.ai')

  useEffect(() => {
    if (!authLoading && (!user || !isAllowed)) {
      navigate('/dashboard')
    }
  }, [user, authLoading, isAllowed, navigate])

  // Auto-run if q param present on load
  useEffect(() => {
    const q = searchParams.get('q')
    if (q && q.length >= 2) {
      setQuery(q)
      setCommitted(q)
      runSearch(q, mode, size)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function runSearch(q: string, searchMode: Mode, pageSize: number) {
    if (!q || q.trim().length < 2) return
    setLoading(true)
    setError('')
    setResults(null)
    setLumenResults(null)

    // Update URL
    setSearchParams({ q: q.trim() })

    try {
      if (searchMode === 'tor') {
        const r = await api.get('/darkweb/search', { params: { q: q.trim(), size: pageSize } })
        setResults(r.data)
      } else {
        const r = await api.get('/darkweb/lumen/search', { params: { q: q.trim(), size: pageSize } })
        setLumenResults(r.data)
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Search failed. Is Elasticsearch running?')
    } finally {
      setLoading(false)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setCommitted(query)
    setSize(20)
    runSearch(query, mode, 20)
  }

  function handleModeSwitch(newMode: Mode) {
    setMode(newMode)
    setResults(null)
    setLumenResults(null)
    if (committed.length >= 2) {
      runSearch(committed, newMode, size)
    }
  }

  function handleLoadMore() {
    const newSize = size + 20
    setSize(newSize)
    runSearch(committed, mode, newSize)
  }

  const totalHits = mode === 'tor'
    ? results?.total_hits ?? 0
    : lumenResults?.total_hits ?? 0

  const isSearched = committed.length >= 2

  if (authLoading) return null

  return (
    <div className="min-h-screen bg-dark-950">
      <Navbar />

      <div className="pt-20 pb-16 px-4">

        {/* ── Header + Search bar ─────────────────────────────────────────── */}
        <div className={`transition-all duration-500 ${isSearched ? 'pt-6' : 'pt-24 text-center'}`}>

          {!isSearched && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="text-2xl">🕸️</span>
                <span className="text-xs font-mono font-bold tracking-widest text-ginger-500 uppercase">
                  Dark Web Index
                </span>
              </div>
              <h1 className="text-4xl font-bold text-white mb-2">Search the Onion Web</h1>
              <p className="text-dark-400 text-sm">
                32,000+ indexed .onion pages · Full-text search · Monadnock Cyber Intelligence
              </p>
            </motion.div>
          )}

          {/* Mode switcher */}
          <div className={`flex gap-2 mb-4 ${isSearched ? '' : 'justify-center'}`}>
            {(['tor', 'lumen'] as Mode[]).map(m => (
              <button
                key={m}
                onClick={() => handleModeSwitch(m)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                  mode === m
                    ? 'bg-ginger-500/20 border-ginger-500/60 text-ginger-400'
                    : 'border-dark-700 text-dark-500 hover:border-dark-500 hover:text-dark-300'
                }`}
              >
                {m === 'tor' ? '🧅 Tor / .onion Index' : '⚖️ Lumen Takedown DB'}
              </button>
            ))}
          </div>

          {/* Search form */}
          <form
            onSubmit={handleSubmit}
            className={`flex gap-2 ${isSearched ? 'max-w-3xl' : 'max-w-2xl mx-auto'}`}
          >
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={
                  mode === 'tor'
                    ? 'Search 32,000+ indexed .onion pages…'
                    : 'Search Lumen takedown notices…'
                }
                className="w-full bg-dark-800 border border-dark-700 rounded-xl pl-10 pr-4 py-3 text-white placeholder-dark-500
                           focus:outline-none focus:border-ginger-500/60 focus:ring-1 focus:ring-ginger-500/30
                           transition-colors font-mono text-sm"
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <button
              type="submit"
              disabled={loading || query.trim().length < 2}
              className="btn btn-primary px-6 rounded-xl shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : 'Search'}
            </button>
          </form>

          {error && (
            <p className="text-red-400 text-sm mt-3 font-mono">{error}</p>
          )}
        </div>

        {/* ── Results area ───────────────────────────────────────────────── */}
        <div className={`mt-6 ${isSearched ? 'max-w-3xl' : 'max-w-2xl mx-auto'}`}>

          {/* Result count bar */}
          {isSearched && !loading && (results || lumenResults) && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-dark-500 font-mono mb-5"
            >
              {totalHits.toLocaleString()} result{totalHits !== 1 ? 's' : ''} for{' '}
              <span className="text-dark-300">"{committed}"</span>
              {mode === 'tor' && (
                <span className="ml-2 text-dark-600">· across 32,000+ indexed .onion pages</span>
              )}
            </motion.p>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="space-y-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-3 bg-dark-700 rounded w-1/3 mb-2" />
                  <div className="h-5 bg-dark-800 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-dark-700 rounded w-full mb-1" />
                  <div className="h-3 bg-dark-700 rounded w-4/5" />
                </div>
              ))}
            </div>
          )}

          {/* Tor results */}
          <AnimatePresence>
            {mode === 'tor' && results && !loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-7"
              >
                {results.results.length === 0 ? (
                  <div className="text-center py-16 text-dark-500">
                    <div className="text-4xl mb-3">🕳️</div>
                    <p className="font-mono text-sm">No results found for "{committed}"</p>
                    <p className="text-xs mt-1">Try different keywords or a phrase in quotes</p>
                  </div>
                ) : (
                  results.results.map((r, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="group"
                    >
                      {/* Domain + URL line */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-dark-600 truncate max-w-xs">
                          {r.domain}
                        </span>
                        {r.updated_on && (
                          <span className="text-xs text-dark-700">·</span>
                        )}
                        {r.updated_on && (
                          <span className="text-xs font-mono text-dark-700">
                            {r.updated_on.split('T')[0]}
                          </span>
                        )}
                        {r.score > 0 && (
                          <>
                            <span className="text-xs text-dark-700">·</span>
                            <span className="text-xs font-mono text-dark-700">
                              score {r.score}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-ginger-400 font-semibold text-base leading-snug mb-1.5 group-hover:text-ginger-300 transition-colors">
                        {r.title && r.title !== '(no title)' ? r.title : (
                          <span className="text-dark-500 italic">Untitled page</span>
                        )}
                      </h3>

                      {/* Snippet */}
                      {r.snippet ? (
                        <p className="text-sm leading-relaxed font-mono">
                          {renderSnippet(r.snippet)}
                        </p>
                      ) : (
                        <p className="text-sm text-dark-600 italic">No preview available</p>
                      )}

                      {/* URL (truncated, non-clickable — .onion only) */}
                      <p className="text-xs font-mono text-dark-700 mt-1.5 truncate">
                        {r.url}
                      </p>
                    </motion.div>
                  ))
                )}

                {/* Load more */}
                {results.results.length > 0 && results.total_hits > results.results.length && (
                  <div className="pt-4 pb-2">
                    <button
                      onClick={handleLoadMore}
                      className="text-sm text-ginger-500 hover:text-ginger-400 font-mono border border-ginger-500/30
                                 hover:border-ginger-500/50 px-6 py-2 rounded-lg transition-colors"
                    >
                      Load more results ({results.results.length} of {results.total_hits.toLocaleString()})
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lumen results */}
          <AnimatePresence>
            {mode === 'lumen' && lumenResults && !loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-5"
              >
                {lumenResults.results.length === 0 ? (
                  <div className="text-center py-16 text-dark-500">
                    <div className="text-4xl mb-3">⚖️</div>
                    <p className="font-mono text-sm">No Lumen notices found for "{committed}"</p>
                  </div>
                ) : (
                  lumenResults.results.map((n, i) => (
                    <motion.div
                      key={n.notice_id || i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="border border-dark-800 rounded-xl p-4 bg-dark-900/40 hover:border-dark-700 transition-colors"
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded border shrink-0 ${LUMEN_COLORS[n.notice_type] || 'text-dark-400 bg-dark-800 border-dark-700'}`}>
                          {n.notice_type}
                        </span>
                        {n.date_received && (
                          <span className="text-xs font-mono text-dark-600 mt-0.5">{n.date_received.split('T')[0]}</span>
                        )}
                        {n.url_count > 0 && (
                          <span className="text-xs font-mono text-dark-600 mt-0.5">{n.url_count} URLs</span>
                        )}
                      </div>

                      <h3 className="text-white font-semibold text-sm mb-1">{n.title}</h3>

                      <div className="flex items-center gap-3 text-xs text-dark-500 font-mono mb-2">
                        <span>From: <span className="text-dark-300">{n.sender_name}</span></span>
                        <span>·</span>
                        <span>To: <span className="text-dark-300">{n.recipient_name}</span></span>
                        {n.action_taken && n.action_taken !== 'Unknown' && (
                          <>
                            <span>·</span>
                            <span className="text-green-600">{n.action_taken}</span>
                          </>
                        )}
                      </div>

                      {n.snippet && (
                        <p className="text-sm font-mono leading-relaxed">
                          {renderSnippet(n.snippet)}
                        </p>
                      )}

                      {n.topics && n.topics.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {n.topics.slice(0, 5).map((t, ti) => (
                            <span key={ti} className="text-xs font-mono text-dark-600 bg-dark-800 px-2 py-0.5 rounded">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))
                )}

                {lumenResults.results.length > 0 && lumenResults.total_hits > lumenResults.results.length && (
                  <div className="pt-4">
                    <button
                      onClick={handleLoadMore}
                      className="text-sm text-ginger-500 hover:text-ginger-400 font-mono border border-ginger-500/30
                                 hover:border-ginger-500/50 px-6 py-2 rounded-lg transition-colors"
                    >
                      Load more ({lumenResults.results.length} of {lumenResults.total_hits.toLocaleString()})
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty initial state hints */}
          {!isSearched && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.3 } }}
              className="mt-10 text-center"
            >
              <p className="text-xs font-mono text-dark-600 mb-4">Try searching for:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {['"leaked credentials"', '"SSN"', '"fullz"', '"bitcoin mixer"', '"hitman"', '"dark market"', '"stealer logs"', '"personal data"'].map(term => (
                  <button
                    key={term}
                    onClick={() => { setQuery(term.replace(/"/g, '')); setCommitted(term.replace(/"/g, '')); runSearch(term.replace(/"/g, ''), mode, 20) }}
                    className="text-xs font-mono text-dark-500 hover:text-ginger-400 border border-dark-800
                               hover:border-ginger-500/30 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-center gap-6 text-xs font-mono text-dark-700">
                <span>32,073+ pages indexed</span>
                <span>·</span>
                <span>Updated daily</span>
                <span>·</span>
                <span>ES full-text + fuzzy match</span>
              </div>
            </motion.div>
          )}

        </div>
      </div>
    </div>
  )
}
